import { Box, Typography } from '@mui/material';
import PatientDetailsCard from 'components/PatientDetailsCard';
import UserAddedToolTip from 'pages/Dashboard/components/UserAddedToolTip';
import {
  ShowMoreDrawerChildProps,
  sliceForShowMore,
} from 'pages/Dashboard/ShowMoreDrawer';
import { DisorderLethal, FamilyHistory } from 'pages/Dashboard/types/familyHistory.types';
import * as React from 'react';

export interface FamilyHistoryCardProps extends ShowMoreDrawerChildProps {
  gridSize?: number;
  familyHistory?: FamilyHistory[];
}

type GroupRelationType = { [key: string]: FamilyHistory[] | [] };
const sliceFamilyHistoryData = (familyHistory: FamilyHistory[]) => {
  const numberOfRecords = 14;
  let count = 0;
  const slicedFamilyHistoryData = familyHistory.map((history) => {
    if (count < numberOfRecords) {
      const newRecords = history.disorders.slice(0, numberOfRecords - count);
      count += newRecords.length;
      return { ...history, disorders: newRecords };
    }
    else return null;
  });
  const finalSlice = slicedFamilyHistoryData.filter((history) => history !== null);
  const groupByRelation = (finalSlice as FamilyHistory[]).reduce((acc: GroupRelationType, obj) => {
    const { relationText: k } = obj;
    const curGroup = acc[k] ?? [];
    return { ...acc, [k]: [...curGroup, obj] };
  }, {});
  return sliceForShowMore(Object.entries(groupByRelation), true);
};

export default function FamilyHistoryCard({
  gridSize,
  drawerMode = false,
  familyHistory = [],
  onShowMore,
}: FamilyHistoryCardProps): JSX.Element | null {
  const groupByRelation = familyHistory.reduce((acc: GroupRelationType, obj) => {
    const { relationText: k } = obj;
    const curGroup = acc[k] ?? [];
    return { ...acc, [k]: [...curGroup, obj] };
  }, {});

  const getDisorderType = (type: DisorderLethal) => {
    switch (type) {
      case DisorderLethal.DISORDER_LETHAL:
        return <Typography variant='body2' color='text.secondary'>(lethal)</Typography>;
      case DisorderLethal.DISORDER_NON_LETHAL:
        return <Typography variant='body2' color='text.secondary'>(non-lethal)</Typography>;
      default:
        return null;
    }
  };

  const data = sliceForShowMore(Object.entries(groupByRelation), !drawerMode);

  const slicedData = sliceFamilyHistoryData(familyHistory);
  const finalData = drawerMode ? data : slicedData;

  return (
    <PatientDetailsCard
      title='Family history'
      showHeader={!drawerMode && familyHistory?.length > 0}
      gridProps={{ xs: 12, md: !drawerMode ? gridSize : undefined }}
      viewDetailsBtnLabel='View all'
      showViewDetails={!drawerMode && Object.entries(groupByRelation)?.length > 3 && !!onShowMore}
      onViewDetails={onShowMore as VoidFunction}
      key='family-history'
      showSubHeader={false}
    >
      {finalData.map((entries) => {
        const [relation, history] = entries;
        return (
          <Box key={`family-history-${history[0].relative.id}`} my={1}>
            <Typography variant='body1' color='text.primary'>
              {relation}
            </Typography>
            {history.map((historyData) => historyData?.disorders.map((disorder) => (
              <React.Fragment key={`disorder-${disorder.id}`}>
                <Box display='flex' flexDirection='row' justifyContent='space-between' my={0.5}>
                  <Box display='flex' gap={1}>
                    <Typography variant='body2' color='text.secondary'>{disorder.name}</Typography>
                    {getDisorderType(disorder.lethal)}
                  </Box>
                  <UserAddedToolTip source={disorder.source} />
                </Box>
              </React.Fragment>
            )))}
          </Box>
        );
      })}
    </PatientDetailsCard>
  );
}
